import { useMemo, useState } from "react";
import { Box, Button, Flex, VStack } from "@chakra-ui/react";
import CDS from "design-system";
import { ColumnDef, createColumnHelper } from "@tanstack/react-table";
import moment from "moment";
import ControlledDataTable from "components/Table/ControlledDataTable";
import { ActionTypeTag } from "design-system";
import { Text12 } from "components/Text";
import { dateInFormat } from "helpers/time";
import { FullActionStatus } from "features/Resources/Shared/Components/ActionStatus";
import { DisplayRecipientsValue } from "features/Resources/Shared/Components/DisplayRecipients";
import { ActionSummary, ActionType, MeetingActionSummary } from "features/Resources/Shared/models";
import {
  filterActionSummaryByText,
  filterByActionType,
  filterByStatusGroup,
  filterNeedsAttention,
  isActionResolved,
} from "features/Resources/Shared/helpers";
import ScheduleListFilters from "./ScheduleListFilters";

interface ScheduleListRow extends ActionSummary {
  __section: string;
}

function computeRelativeDay(actionDate: Date | undefined, startDate: string | undefined): string {
  if (!actionDate || !startDate) return "—";
  const start = moment(startDate).startOf("day");
  const action = moment(actionDate).startOf("day");
  const diff = action.diff(start, "days");
  if (diff < 0) return `Day ${diff}`;
  if (diff === 0) return "Start date";
  return `Day ${diff}`;
}

/**
 * Compute a week-based section label from an action's date relative to the start date.
 * Weeks 1–4 show just "Week N", weeks 5+ add the month in parentheses.
 */
function buildSectionLabel(action: ActionSummary, startDate: string | undefined): string {
  if (action.unifiedIsImmediate) return "Immediate";
  if (action.unifiedDependencyId) return "After Task Completion";
  if (action.stageKey === "preboarding") return "Preboarding";

  if (!startDate || !action.unifiedDate) return "Scheduled";

  const start = moment(startDate).startOf("isoWeek"); // Monday of start week
  const actionDate = moment(action.unifiedDate).startOf("isoWeek");
  const weekNum = actionDate.diff(start, "weeks") + 1;

  if (weekNum < 1) return "Preboarding";

  const mon = actionDate.clone();
  const sun = mon.clone().add(6, "days");
  const range = `${mon.format("MMM D")} – ${sun.format("MMM D")}`;

  // Compute which month period using calendar months from start date
  const monthNum = moment(action.unifiedDate).startOf("day").diff(moment(startDate).startOf("day"), "months") + 1;

  if (monthNum >= 2) {
    return `Week ${weekNum} (Month ${monthNum}) · ${range}`;
  }
  return `Week ${weekNum} · ${range}`;
}
function isCrossedOut(action: ActionSummary): boolean {
  return isActionResolved(action.action);
}

const columnHelper = createColumnHelper<ScheduleListRow>();

function createScheduleListColumns(
  onActionClick: (action: ActionSummary) => void,
  startDate: string | undefined,
  timezone: string,
): ColumnDef<ScheduleListRow, any>[] {
  return [
    { accessorKey: "__section" },
    columnHelper.accessor("actionType", {
      id: "type",
      header: "",
      size: 40,
      minSize: 40,
      maxSize: 40,
      meta: { sx: { paddingRight: 0, paddingLeft: "12px", width: "40px", maxWidth: "40px" } },
      cell: ({ row }) => {
        const meetingType = row.original.actionType === "meeting" ? (row.original as MeetingActionSummary).action?.meetingType : undefined;
        return <ActionTypeTag.IconBadgeWithMeetingType actionType={row.original.actionType} meetingType={meetingType} />;
      },
    }),
    columnHelper.accessor((row) => row.action?.name, {
      id: "name",
      header: "Name",
      meta: { sx: { paddingLeft: 0 } },
      cell: ({ getValue, row }) => (
        <VStack align="start" spacing={0}>
          <Button
            variant="link"
            color="brand.500"
            fontWeight="medium"
            fontSize="sm"
            onClick={() => onActionClick(row.original)}
            _hover={{ textDecoration: "underline" }}
            textDecoration={isCrossedOut(row.original) ? "line-through" : "none"}
            height="auto"
            minH={0}
          >
            {getValue()}
          </Button>
          <CDS.Text10 color="fg.muted" noOfLines={1}>
            <DisplayRecipientsValue action={row.original} />
          </CDS.Text10>
        </VStack>
      ),
    }),
    columnHelper.accessor("unifiedDate", {
      id: "when",
      header: "When",
      size: 130,
      minSize: 130,
      maxSize: 130,
      cell: ({ getValue }) => {
        const date = getValue();
        return <Text12 color="fg.muted">{date ? dateInFormat(date, "M/D/YY h:mma", timezone) : "—"}</Text12>;
      },
    }),
    columnHelper.accessor("unifiedDate", {
      id: "day",
      header: "Day",
      size: 100,
      minSize: 100,
      maxSize: 100,
      cell: ({ getValue, row }) => {
        if (row.original.unifiedIsImmediate) {
          return <Text12 color="fg.muted">Immediate</Text12>;
        }
        if (row.original.unifiedDependencyId) {
          return <Text12 color="fg.muted">After task</Text12>;
        }
        return <Text12 color="fg.muted">{computeRelativeDay(getValue(), startDate)}</Text12>;
      },
    }),
    columnHelper.accessor((row) => row.action?.status, {
      id: "status",
      header: "Status",
      size: 140,
      minSize: 140,
      maxSize: 140,
      cell: ({ row }) => <FullActionStatus action={row.original} />,
    }),
  ];
}

function EmptyScheduleList({ isFiltered }: { isFiltered: boolean }) {
  return (
    <Box textAlign="center" py="8">
      <Text12>{isFiltered ? "No actions match these filters." : "Nothing to show for this New Hire."}</Text12>
    </Box>
  );
}

interface ScheduleListViewProps {
  actions: ActionSummary[];
  startDate: string | undefined;
  timezone: string;
  onActionClick: (action: ActionSummary) => void;
  isComplete?: boolean;
}

export default function ScheduleListView({
  actions,
  startDate,
  timezone,
  onActionClick,
  isComplete = false,
}: ScheduleListViewProps) {
  const [searchText, setSearchText] = useState("");
  const [needsAttention, setNeedsAttention] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined);
  const [typeFilter, setTypeFilter] = useState<string | undefined>(undefined);

  const needsAttentionCount = useMemo(() => filterNeedsAttention(actions).length, [actions]);

  const filteredActions = useMemo(() => {
    let result = filterActionSummaryByText(actions, searchText);
    result = filterByActionType(result, (typeFilter as ActionType) || "");
    result = filterByStatusGroup(result, statusFilter || "");
    if (needsAttention) result = filterNeedsAttention(result);
    return result;
  }, [actions, searchText, typeFilter, statusFilter, needsAttention]);

  const handleReset = () => {
    setSearchText("");
    setNeedsAttention(false);
    setStatusFilter(undefined);
    setTypeFilter(undefined);
  };

  // Sort actions: special sections first (immediate, dependency, preboarding), then by date
  const sectionedData = useMemo(() => {
    const sectionOrder = (a: ActionSummary): number => {
      if (a.unifiedIsImmediate) return 0;
      if (a.unifiedDependencyId) return 1;
      if (a.stageKey === "preboarding") return 2;
      return 3;
    };

    const sorted = [...filteredActions].sort((a, b) => {
      const orderA = sectionOrder(a);
      const orderB = sectionOrder(b);
      if (orderA !== orderB) return orderA - orderB;
      const dateA = a.unifiedDate ? new Date(a.unifiedDate).getTime() : 0;
      const dateB = b.unifiedDate ? new Date(b.unifiedDate).getTime() : 0;
      return dateA - dateB;
    });

    return sorted.map((action) => ({
      ...action,
      __section: isComplete ? "Onboarding Complete" : buildSectionLabel(action, startDate),
    }));
  }, [filteredActions, startDate, isComplete]);

  const columns = useMemo(
    () => createScheduleListColumns(onActionClick, startDate, timezone),
    [onActionClick, startDate, timezone],
  );

  const isFiltered = !!searchText || needsAttention || !!statusFilter || !!typeFilter;

  return (
    <Flex direction="column" gap="4">
      <ScheduleListFilters
        searchText={searchText}
        onSearchChange={setSearchText}
        needsAttention={needsAttention}
        onNeedsAttentionChange={setNeedsAttention}
        needsAttentionCount={needsAttentionCount}
        statusFilter={statusFilter}
        onStatusChange={setStatusFilter}
        typeFilter={typeFilter}
        onTypeChange={setTypeFilter}
        onReset={handleReset}
      />
      <ControlledDataTable<ScheduleListRow>
        data={sectionedData}
        columns={columns}
        isLoading={false}
        emptyStateComponent={<EmptyScheduleList isFiltered={isFiltered} />}
        initialPagination={{ pageIndex: 0, pageSize: 100 }}
      />
    </Flex>
  );
}
